import {
  Button,
  FormInput,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { z } from "zod";
import type {
  CategoryCreateInput,
  CategoryUpdateInput,
} from "@/modules/categories/domain/contracts";

const CategoryFormSchema = z.object({
  name: z.string().min(1, "ກະລຸນາໃສ່ຊື່ຫມວດຫມູ່"),
});

export type CategoryFormValues = z.infer<typeof CategoryFormSchema>;

type CategoryFormProps = {
  initialValues?: Partial<CategoryFormValues>;
  /** Fixed parent (read-only banner). Not editable in the form. */
  parentId?: string | null;
  parentName?: string | null;
  onSubmit: (values: CategoryCreateInput | CategoryUpdateInput) => void;
  onCancel?: () => void;
  submitting?: boolean;
};

export function CategoryForm({
  initialValues,
  parentId,
  parentName,
  onSubmit,
  onCancel,
  submitting,
}: CategoryFormProps) {
  const methods = RHF.useForm<CategoryFormValues>({
    resolver: zodResolver(CategoryFormSchema),
    defaultValues: {
      name: initialValues?.name ?? "",
    },
  });

  const handleSubmit = (vals: CategoryFormValues) => {
    onSubmit({
      name: vals.name,
      parentId: parentId ?? null,
    });
  };

  const hasParent = Boolean(parentId && parentName);

  return (
    <FormRoot<CategoryFormValues>
      methods={methods}
      onSubmit={handleSubmit}
      className="space-y-5"
    >
      {hasParent ? (
        <div className="rounded-lg border border-border/70 bg-muted/30 px-3 py-2.5 text-sm">
          <p className="text-muted-foreground text-xs">ເປັນຫມວດຍ່ອຍຂອງ</p>
          <p className="mt-0.5 font-medium text-foreground">{parentName}</p>
        </div>
      ) : null}

      <FormInput name="name" label="ຊື່ຫມວດຫມູ່" placeholder="ກະລຸນາໃສ່ຊື່" />

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        )}
        <Button type="submit" disabled={submitting}>
          ບັນທຶກ
        </Button>
      </div>
    </FormRoot>
  );
}
